import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import BaseIcon from "../components/svg_icons/BaseIcon";
import "../css/utils/ActionButtons.css"

const BUTTON_CONFIG = {
    // Text based buttons
    view:           { label: "View",     className: "universal-actions-btn-view" },
    viewSecondary:  { label: "View",     className: "universal-actions-btn-view-secondary" },
    viewOutline:    { label: "View",     className: "universal-actions-btn-view-outline" },
    edit:           { label: "Edit",     className: "universal-actions-btn-edit" },
    cancel:         { label: "Cancel",   className: "universal-actions-btn-cancel" },
    delete:         { label: "Delete",   className: "universal-actions-btn-delete" },
    reminder:       { label: "Reminder", className: "universal-actions-btn-warning" },
    complete:       { label: "Complete", className: "universal-actions-btn-success" },
    
    // Icon based buttons
    moreOptions:    {
        icon: "moreDown", iconColor: "#3c3c43", iconSize: 25,
        className: "universal-actions-btn-moreOptions"
    },
    deleteIcon: { icon: "trash", iconSize: 20, className: "universal-actions-btn-delete" },
    editIcon:   { icon: "eyeOpen", iconSize: 20, className: "universal-actions-btn-view" },
};

const ActionButtons = ({ actions = [] }) => {
    const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
    const [dropdownCoords, setDropdownCoords] = useState({ top: 0, left: 0 });
    
    // NEW: Tracks if the dropdown was opened by a click (makes it stay open)
    const [isClickedOpen, setIsClickedOpen] = useState(false); 

    const containerRef = useRef(null);
    const timeoutRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                containerRef.current && 
                !containerRef.current.contains(event.target) && 
                !event.target.closest('.universal-actions-dropdown-menu')
            ) {
                closeDropdown();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    const closeDropdown = () => {
        setOpenDropdownIndex(null);
        setIsClickedOpen(false);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };

    const updateCoords = (target) => {
        const rect = target.getBoundingClientRect();
        setDropdownCoords({
            top: rect.bottom + window.scrollY,
            left: rect.right + window.scrollX - 150
        });
    };

    // 1. CLICK FUNCTIONALITY
    const toggleDropdown = (index, event, originalOnClick) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        
        // If it's already open but only via hover, a click "locks" it open
        if (openDropdownIndex === index && !isClickedOpen) {
            setIsClickedOpen(true);
        } 
        // If it's already locked open by a click, close it
        else if (openDropdownIndex === index && isClickedOpen) {
            closeDropdown();
        } 
        // If it's completely closed, open and lock it
        else {
            updateCoords(event.currentTarget);
            setOpenDropdownIndex(index);
            setIsClickedOpen(true);
        }
        if (originalOnClick) originalOnClick();
    };

    // 2. HOVER FUNCTIONALITY
    const handleMouseEnter = (index, event) => {
        if (isClickedOpen) return; // Ignore hover rules if user locked it with a click
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        updateCoords(event.currentTarget);
        setOpenDropdownIndex(index);
    };

    const handleMouseLeave = () => {
        if (isClickedOpen) return; // Keep it open on mouse leave if clicked
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => { closeDropdown(); }, 150);
    };

    return (
        <div className="universal-actions-buttons" ref={containerRef}>
            {actions.map((action, index) => {
                const config = BUTTON_CONFIG[action.buttonType];
                if (!config) return null;

                const isDropdownOpen = openDropdownIndex === index;
                const isMoreOptions = action.buttonType === "moreOptions";

                return (
                    <div 
                        key={index} 
                        className="universal-actions-btn-container" 
                        style={{ display: 'inline-block' }}
                        onMouseEnter={(e) => isMoreOptions && handleMouseEnter(index, e)}
                        onMouseLeave={() => isMoreOptions && handleMouseLeave()}
                    >
                        <button
                            type="button"
                            className={`universal-actions-btn ${config.className} ${isDropdownOpen ? 'is-open' : ''}`}
                            onClick={(e) => isMoreOptions ? toggleDropdown(index, e, action.onClick) : action.onClick?.()}
                        >
                            {config.icon && (
                                <span className="universal-actions-icon-wrap">
                                    <BaseIcon
                                        name  = { config.icon }
                                        color = { action.iconColor || config.iconColor }
                                        size  = { action.iconSize  || config.iconSize }
                                    />
                                </span>
                            )}
                            {(action.customName || config.label) && (
                                <span>{action.customName || config.label}</span>
                            )}
                        </button>

                        {isMoreOptions && isDropdownOpen && action.dropdownOptions && createPortal(
                            <div 
                                className="universal-actions-dropdown-menu"
                                style={{
                                    position: 'absolute',
                                    top: `${dropdownCoords.top}px`,
                                    left: `${dropdownCoords.left}px`,
                                    margin: 0
                                }}
                                onMouseEnter={() => {
                                    if (isClickedOpen) return;
                                    if (timeoutRef.current) clearTimeout(timeoutRef.current);
                                }}
                                onMouseLeave={handleMouseLeave}
                            >
                                {action.dropdownOptions.map((option, optIndex) => (
                                    <button
                                        key={optIndex}
                                        type="button"
                                        className="universal-actions-dropdown-item"
                                        onClick={() => {
                                            option.onClick?.();
                                            closeDropdown();
                                        }}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>,
                            document.body
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default ActionButtons;
